import { ArrowRight, ArrowDown, Car, Sunrise, Clock } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { ImageSlot } from "./ImageSlot";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export const Eyebrow = ({ children, light }) => (
  <div className={`flex items-center gap-3 text-[10px] sm:text-[11px] uppercase tracking-[0.32em] ${light ? "text-[#D9C79E]" : "text-[#9C7F45]"}`}>
    <span className={`h-px w-8 ${light ? "bg-[#D9C79E]" : "bg-[#B89A5E]"}`} />
    {children}
  </div>
);

const Paras = ({ items = [], className = "" }) => (
  <div className={`space-y-5 ${className}`}>
    {items.map((p, i) => (
      <p key={i} className="text-sm sm:text-base leading-[1.9] text-[#2A1F27]/75">{p}</p>
    ))}
  </div>
);

export const BridalHero = ({ hero, onEnquire, onMenu }) => (
  <section data-testid="bridal-hero" className="relative pt-32 sm:pt-40 pb-16 sm:pb-24">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
      <div className="lg:col-span-6">
        <Reveal><Eyebrow>{hero.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 data-testid="bridal-hero-title" className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.2rem] text-[#2A1F27] mt-6 tracking-tight">
            {hero.title}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-md text-sm sm:text-base leading-[1.9] text-[#2A1F27]/70">{hero.subtitle}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button data-testid="bridal-hero-enquire-btn" onClick={onEnquire}
              className="group inline-flex items-center justify-center gap-3 bg-[#2A1F27] text-[#FAF7F2] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-[#9C7F45]">
              {hero.cta_primary}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="bridal-hero-menu-btn" onClick={onMenu}
              className="inline-flex items-center justify-center gap-3 border border-[#2A1F27]/25 text-[#2A1F27] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:border-[#2A1F27]">
              {hero.cta_secondary}
            </button>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="lg:col-span-6">
        <ImageSlot src={hero.image_url} alt="Bridal hair by Malek Dridi" label="Hero bridal portrait" testId="bridal-hero-image" className="aspect-[4/5] w-full" />
      </Reveal>
    </div>
  </section>
);

export const BridalIntro = ({ intro }) => (
  <section data-testid="bridal-intro" className="py-20 sm:py-32 bg-[#F3EEE6]">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
      <Reveal className="lg:col-span-5 order-2 lg:order-1">
        <ImageSlot src={intro.image_url} label="Bridal detail / portrait" testId="bridal-intro-image" className="aspect-[3/4] w-full max-w-md" />
      </Reveal>
      <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
        <Reveal><Eyebrow>{intro.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6 leading-[1.08]">{intro.heading}</h2>
        </Reveal>
        <Reveal delay={0.2}><Paras items={intro.body} className="mt-8 max-w-xl" /></Reveal>
      </div>
    </div>
  </section>
);

export const BridalExperience = ({ experience }) => (
  <section data-testid="bridal-experience" className="py-20 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
      <Reveal><Eyebrow>{experience.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6 max-w-2xl leading-[1.08]">{experience.heading}</h2>
      </Reveal>
      <Stagger className="mt-14 sm:mt-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A1F27]/10 border border-[#2A1F27]/10">
        {(experience.stages || []).map((s, i) => (
          <Item key={i} className="group bg-[#FAF7F2] p-8 sm:p-10 transition-colors duration-500 hover:bg-white">
            <div data-testid={`bridal-stage-${i}`}>
              <div className="font-display italic text-5xl text-[#B89A5E]/70 transition-colors duration-500 group-hover:text-[#9C7F45]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-6 font-display text-2xl text-[#2A1F27]">{s.title}</h3>
              <p className="mt-3 text-sm leading-[1.85] text-[#2A1F27]/70 max-w-sm">{s.body}</p>
            </div>
          </Item>
        ))}
      </Stagger>
    </div>
  </section>
);

export const BridalMenu = ({ menu, onEnquire, menuRef }) => (
  <section ref={menuRef} data-testid="bridal-menu" className="py-20 sm:py-32 bg-[#2A1F27] text-[#FAF7F2] scroll-mt-24">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><Eyebrow light>{menu.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.08]">{menu.heading}</h2></Reveal>
        <Reveal delay={0.2}><p className="mt-6 text-sm leading-[1.9] text-[#FAF7F2]/65 max-w-sm">{menu.intro}</p></Reveal>
        <Reveal delay={0.3}>
          <button data-testid="bridal-menu-enquire-btn" onClick={onEnquire}
            className="mt-10 inline-flex items-center gap-3 border border-[#D9C79E]/60 text-[#D9C79E] px-7 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-[#D9C79E] hover:text-[#2A1F27]">
            Check your wedding date <ArrowRight size={14} />
          </button>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        <Stagger>
          {(menu.items || []).map((m, i) => (
            <Item key={i}>
              {m.group && m.group !== menu.items[i - 1]?.group && (
                <div data-testid={`bridal-menu-group-${i}`} className={`${i ? "mt-14" : ""} mb-3 font-display italic text-2xl sm:text-3xl text-[#D9C79E]`}>{m.group}</div>
              )}
              <div data-testid={`bridal-menu-item-${i}`} className="py-6 border-b border-[#FAF7F2]/10 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8">
                <div className="flex-1">
                  <div className="font-display text-xl sm:text-2xl">{m.name}</div>
                  {m.description && <p className="mt-2 text-xs sm:text-sm leading-[1.8] text-[#FAF7F2]/55 max-w-md">{m.description}</p>}
                </div>
                <div data-testid={`bridal-menu-price-${i}`} className="text-[11px] uppercase tracking-[0.24em] text-[#D9C79E] whitespace-nowrap">{m.price}</div>
              </div>
            </Item>
          ))}
        </Stagger>
        {(menu.notes || []).length > 0 && (
          <ul className="mt-8 space-y-2" data-testid="bridal-menu-notes">
            {menu.notes.map((n, i) => (
              <li key={i} className="text-xs leading-[1.8] text-[#FAF7F2]/55">* {n}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </section>
);

export const BridalExtensions = ({ extensions }) => (
  <section data-testid="bridal-extensions" className="py-20 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
      <div className="lg:col-span-6">
        <Reveal><Eyebrow>{extensions.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6 leading-[1.08]">{extensions.heading}</h2></Reveal>
        <Reveal delay={0.2}><Paras items={extensions.body} className="mt-8 max-w-xl" /></Reveal>
        <Reveal delay={0.3}>
          <ul className="mt-10 space-y-4">
            {(extensions.points || []).map((p, i) => (
              <li key={i} className="flex items-start gap-4 text-sm text-[#2A1F27]/80">
                <span className="mt-2 h-px w-6 bg-[#B89A5E] shrink-0" />{p}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
        <ImageSlot src={extensions.image_url} label="Bridal extensions result" testId="bridal-extensions-image" className="aspect-[4/5] w-full" />
      </Reveal>
    </div>
  </section>
);

export const BridalMorning = ({ morning }) => (
  <section data-testid="bridal-morning" className="py-20 sm:py-32 bg-[#F3EEE6]">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
      <Reveal>
        <ImageSlot src={morning.image_url} label="Candid wedding-morning moment" testId="bridal-morning-image" className="aspect-[4/5] sm:aspect-[21/9] w-full" />
      </Reveal>
      <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow><Sunrise size={12} />{morning.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6 leading-[1.08]">{morning.heading}</h2></Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7"><Paras items={morning.body} /></Reveal>
      </div>
    </div>
  </section>
);

export const BridalMobile = ({ mobile }) => (
  <section data-testid="bridal-mobile" className="py-20 sm:py-32">
    <div className="mx-auto max-w-[1100px] px-5 sm:px-10 text-left sm:text-center">
      <Reveal><div className="sm:flex sm:justify-center"><Eyebrow><Car size={12} />{mobile.eyebrow}</Eyebrow></div></Reveal>
      <Reveal delay={0.1}><h2 className="font-display text-5xl sm:text-7xl text-[#2A1F27] mt-6 italic">{mobile.heading}</h2></Reveal>
      <Reveal delay={0.2}><Paras items={mobile.body} className="mt-8 max-w-2xl sm:mx-auto" /></Reveal>
      <Stagger className="mt-14 grid sm:grid-cols-3 gap-px bg-[#2A1F27]/10 border border-[#2A1F27]/10">
        {(mobile.points || []).map((p, i) => (
          <Item key={i} className="bg-[#FAF7F2] p-8 text-left">
            <Clock size={16} strokeWidth={1.3} className="text-[#B89A5E]" />
            <p className="mt-4 text-sm leading-[1.8] text-[#2A1F27]/80">{p}</p>
          </Item>
        ))}
      </Stagger>
    </div>
  </section>
);

export const BridalGallery = ({ gallery, images }) => {
  const count = Math.max(Number(gallery.placeholder_count) || 0, 0);
  const slots = [...images, ...Array.from({ length: Math.max(count - images.length, 0) }, () => null)];
  return (
    <section data-testid="bridal-gallery" className="py-20 sm:py-32 bg-[#F3EEE6]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <Reveal><Eyebrow>{gallery.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6">{gallery.heading}</h2></Reveal>
          </div>
          {gallery.intro && <Reveal delay={0.2}><p className="text-sm text-[#2A1F27]/60 max-w-xs">{gallery.intro}</p></Reveal>}
        </div>
        <div className="mt-12 columns-2 lg:columns-3 gap-3 sm:gap-5 [&>*]:mb-3 sm:[&>*]:mb-5">
          {slots.map((img, i) => (
            <Reveal key={img?.id || i} delay={(i % 3) * 0.08} className="break-inside-avoid">
              <figure data-testid={`bridal-gallery-item-${i}`}>
                <ImageSlot
                  src={img?.url}
                  alt={img?.caption || "Bridal hair"}
                  label={`Bridal photo ${i + 1}`}
                  className={img?.orientation === "landscape" ? "aspect-[4/3]" : img?.orientation === "square" ? "aspect-square" : i % 3 === 1 ? "aspect-[3/4]" : "aspect-[4/5]"}
                />
                {img?.caption && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#2A1F27]/50">{img.caption}</figcaption>}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const BridalFaq = ({ faqs }) => (
  <section data-testid="bridal-faq" className="py-20 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <Reveal><Eyebrow>{faqs.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6">{faqs.heading}</h2></Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
        <Accordion type="single" collapsible className="border-t border-[#2A1F27]/10">
          {(faqs.items || []).map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-b border-[#2A1F27]/10">
              <AccordionTrigger data-testid={`bridal-faq-trigger-${i}`} className="py-6 text-left font-display text-lg sm:text-xl text-[#2A1F27] hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`bridal-faq-answer-${i}`} className="pb-6 text-sm leading-[1.9] text-[#2A1F27]/70 max-w-2xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const BridalCta = ({ cta, onEnquire }) => (
  <section data-testid="bridal-cta" className="py-24 sm:py-36 bg-[#2A1F27] text-[#FAF7F2] text-center">
    <div className="mx-auto max-w-3xl px-5">
      <Reveal><h2 className="font-display text-4xl sm:text-6xl leading-[1.05] italic">{cta.heading}</h2></Reveal>
      <Reveal delay={0.1}><p className="mt-6 text-sm sm:text-base leading-[1.9] text-[#FAF7F2]/65">{cta.body}</p></Reveal>
      <Reveal delay={0.2}>
        <button data-testid="bridal-cta-enquire-btn" onClick={onEnquire}
          className="mt-10 inline-flex items-center gap-3 bg-[#D9C79E] text-[#2A1F27] px-9 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-[#FAF7F2]">
          {cta.button} <ArrowDown size={14} />
        </button>
      </Reveal>
    </div>
  </section>
);
